import type { ComponentType } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import type { NumberFormatValues, NumericFormatProps } from 'react-number-format'
import { NumericFormat } from 'react-number-format'
import type { SxProps, TextFieldProps, Theme } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { inputBaseClasses } from '@mui/material/InputBase'
import TextField from '@mui/material/TextField'
import isNil from 'lodash-es/isNil'

import { useI18nFormatters } from '../hooks/useI18nFormatters'

type CompatibleTextFieldProps<TInputProps = TextFieldProps> = Omit<TInputProps, 'value' | 'defaultValue' | 'onChange'>

const CONTAIN_INPUT_SX: SxProps<Theme> = {
  [`& .${inputBaseClasses.input}`]: {
    boxSizing: 'border-box',
    height: '100%',
  },
}

// MUI gives the input a text cursor, which hides that the whole field is clickable.
const CLICKABLE_SX: SxProps<Theme> = {
  [`& .${inputBaseClasses.root}, & .${inputBaseClasses.input}`]: { cursor: 'pointer' },
}

/**
 * CurrencyTextField props other than `currency`, for wrappers that supply it.
 */
export type CurrencyTextFieldBaseProps<TInputProps = TextFieldProps> = CompatibleTextFieldProps<TInputProps> &
  Pick<NumericFormatProps, 'decimalScale' | 'fixedDecimalScale' | 'allowNegative'> & {
    /** Numeric value (controlled mode). Use `null` for empty state. */
    value?: number | null
    /** Initial numeric value (uncontrolled mode). Use `null` for empty state. */
    defaultValue?: number | null
    /** Number of decimal places to display. */
    decimalScale?: number
    /** Whether to always show decimal places. @default true */
    fixedDecimalScale?: boolean
    /** Whether to allow negative numbers. @default true */
    allowNegative?: boolean
    /** Custom input component to use instead of MUI TextField. */
    InputComponent?: ComponentType<CompatibleTextFieldProps>
    /**
     * Callback when the value changes.
     * @param values - Object containing `floatValue`, `formattedValue`, and other formatting details
     * @param sourceInfo - Source information including the triggering event
     */
    onChange?: (values: NumberFormatValues, sourceInfo: { event?: React.SyntheticEvent }) => void
  }

/**
 * Props for CurrencyTextField component
 */
export type CurrencyTextFieldProps<TInputProps = TextFieldProps> = CurrencyTextFieldBaseProps<TInputProps> & {
  /** {@link https://en.wikipedia.org/wiki/ISO_4217 ISO 4217} 3-letter currency code. @default 'USD' */
  currency?: string
}

/**
 * A locale-aware currency input field that formats numbers with thousands separators and currency symbols.
 *
 * Features:
 * - Automatic formatting based on user's locale (via react-intl)
 * - Supports both controlled and uncontrolled modes
 * - Configurable currency symbol (ISO 4217 codes)
 * - Customizable decimal places and formatting options
 * - Returns numeric values directly via onChange callback
 *
 * @example
 * ```tsx
 * // Controlled
 * <CurrencyTextField
 *   currency="EUR"
 *   value={budget}
 *   onChange={(values) => setBudget(values.floatValue ?? 0)}
 * />
 *
 * // Uncontrolled
 * <CurrencyTextField
 *   defaultValue={1000}
 *   decimalScale={2}
 * />
 * ```
 */
export const CurrencyTextField = <TInputProps extends TextFieldProps = TextFieldProps>({
  value,
  defaultValue,
  currency,
  onChange,
  decimalScale,
  fixedDecimalScale = true,
  allowNegative = true,
  InputComponent = TextField,
  ...textFieldProps
}: CurrencyTextFieldProps<TInputProps>) => {
  const intl = useIntl()
  const { getFormattedCurrencySymbol } = useI18nFormatters({ currency })
  const [internalValue, setInternalValue] = useState<number | null>(defaultValue ?? value ?? null)

  // Determine if component is controlled
  const isControlled = value !== undefined

  const currencySymbol = getFormattedCurrencySymbol()

  // Get formatting configuration from Intl.NumberFormat
  const { decimalSeparator, thousandSeparator } = useMemo(() => {
    const formatter = new Intl.NumberFormat(intl.locale, { style: 'decimal' })

    const parts = formatter.formatToParts(1234.56)
    const decimal = parts.find((part) => part.type === 'decimal')?.value || '.'
    const group = parts.find((part) => part.type === 'group')?.value || ','

    return {
      decimalSeparator: decimal,
      thousandSeparator: group,
    }
  }, [intl.locale])

  // Sync external value changes to internal state (only in controlled mode)
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value ?? null)
    }
  }, [value])

  const handleValueChange = useCallback(
    (values: NumberFormatValues, sourceInfo: { event?: React.SyntheticEvent }) => {
      const { floatValue } = values
      const numericValue = floatValue ?? null

      // Update internal state in uncontrolled mode
      if (!isControlled) {
        setInternalValue(numericValue)
      }

      // Call the onChange handler if provided
      if (onChange) {
        onChange(values, sourceInfo)
      }
    },
    [onChange, isControlled]
  )

  const callerSx = textFieldProps.sx

  return (
    <NumericFormat<CompatibleTextFieldProps>
      // NumericFormat props
      onValueChange={handleValueChange}
      customInput={InputComponent}
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
      decimalScale={decimalScale}
      fixedDecimalScale={fixedDecimalScale}
      allowNegative={allowNegative}
      valueIsNumericString={false}
      // TextField props
      {...textFieldProps}
      InputProps={{
        ...textFieldProps.InputProps,
        startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
      }}
      inputProps={{
        ...textFieldProps.inputProps,
        'data-value': isControlled ? value : internalValue,
      }}
      sx={[
        CONTAIN_INPUT_SX,
        textFieldProps.onClick ? CLICKABLE_SX : null,
        ...(Array.isArray(callerSx) ? callerSx : [callerSx]),
      ]}
      type="text"
      value={isControlled ? (value ?? '') : (internalValue ?? undefined)}
      defaultValue={!isNil(defaultValue) ? defaultValue : undefined}
    />
  )
}
CurrencyTextField.displayName = 'CurrencyTextField'
